import { DataProxy } from 'apollo-cache';
import { FetchResult } from 'apollo-link';
import produce, { Draft } from 'immer';
import { find, findIndex } from 'lodash';
import { ExecutionResult } from 'graphql';

import { FeedKey, GetGroupFollowersDocument, Group, GroupRole, UserFeedFollow } from 'api';
import { GROUP_MEMBERS_LIMIT } from 'constants/index';

export type GroupMemberFollowReducers =
  | 'addMember'
  | 'removeMember'
  | 'memberToModerator'
  | 'moderatorToMember';

type ReducerArgs = (
  feedByUser: UserFeedFollow[],
  results: ExecutionResult['data'],
  targetUserId: string,
) => void;

interface IGroupMemberFollowOptions {
  group: Group;
  targetUserId?: string;
}

interface IReducer {
  name: keyof IReducers;
  cache: DataProxy;
  result: FetchResult;
  variables: IGroupMemberFollowOptions;
}

interface IReducers {
  addMember: ReducerArgs;
  removeMember: ReducerArgs;
  memberToModerator: ReducerArgs;
  moderatorToMember: ReducerArgs;
}

const reducers: IReducers = {
  addMember: produce((draft: Draft<UserFeedFollow[]>, result) => {
    draft.unshift(result);
    return draft;
  }),
  removeMember: produce((draft: Draft<UserFeedFollow[]>, result) => {
    const index = findIndex(draft, { followed_by_user: { id: result.user_id } });
    draft.splice(index, 1);
    return draft;
  }),
  memberToModerator: produce((draft: Draft<UserFeedFollow[]>, result) => {
    const target = find(draft, { user_id: result.user_id });
    const index = findIndex(draft, result.user_id);
    target.group_role = GroupRole.Moderator;
    draft[index] = target;
    return draft;
  }),
  moderatorToMember: produce((draft: Draft<UserFeedFollow[]>, result, targetUserId) => {
    const target = find(draft, { user_id: targetUserId });
    const index = findIndex(draft, targetUserId);
    target.group_role = GroupRole.Member;
    draft[index] = target;
    return draft;
  }),
};

export default (options: IReducer) => {
  const { name, cache, result, variables } = options;
  const cacheOptions = {
    query: GetGroupFollowersDocument,
    variables: {
      data: { id: variables.group.id, limit: GROUP_MEMBERS_LIMIT, feed: FeedKey.Group },
    },
  };

  const { feedFollowing } = cache.readQuery(cacheOptions);

  if (!feedFollowing) {
    return;
  }

  const mutations = Object.keys(result.data);

  mutations.forEach(mutation => {
    const updated = reducers[name](
      feedFollowing.feed_follows,
      result.data[mutation],
      variables.targetUserId,
    );

    cache.writeQuery({
      ...cacheOptions,
      data: { feedFollowing: { ...feedFollowing, feed_follows: updated } },
    });
  });
};
